import { useState, useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { message } from "antd";
import { XMarkIcon, ArrowUpIcon } from "@heroicons/react/24/solid";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import {
  cld,
  handleFileUpload,
  MAX_VIDEO_SIZE_MB,
} from "../../../utils/cloudinary";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_VIDEO, GET_VIDEO } from "../../../utils/api/video";
import { hasProcessingStalled, shouldPollVideo, videoInputState } from "../../../utils/video-utils";
import Spinner, { SPINNER_SIZES } from "../../icons/Spinner";

interface VideoInputProps {
  name: string,
  readOnly?: boolean,
  required?: boolean,
}

const POLL_MS = 3000;
// Once we've admitted the video is slow there's no value in asking every 3s, and each
// errored poll costs a Rollbar report.
const STALLED_POLL_MS = 30000;
// Only needs to be fine-grained relative to the 5-minute ceiling.
const CLOCK_TICK_MS = 15000;

const CONTAINER_CLASS =
  "bg-white max-w-lg flex flex-col justify-center px-6 pt-5 pb-6 h-30 border-2 border-dashed rounded-md relative place-content-center";

const VideoInput: React.FC<VideoInputProps> = ({ name, readOnly = false, required = false }) => {
  const { register, setValue, watch } = useFormContext();
  const videoId = watch(name);
  const [createVideo] = useMutation(CREATE_VIDEO);
  const [uploading, setUploading] = useState(false);

  // errorPolicy 'all' keeps a partial response usable, so a transient error doesn't
  // discard the status we already have.
  const { data, loading, error, startPolling, stopPolling } = useQuery(GET_VIDEO, {
    variables: { id: videoId },
    skip: !videoId,
    errorPolicy: 'all',
  });

  // When we started waiting on this particular video. Only a fallback: the server's
  // createdAt is preferred below. It exists because a query that keeps failing never
  // yields a createdAt, and without any anchor that wait could never resolve.
  const [waitStartedAt, setWaitStartedAt] = useState<number | null>(null);
  useEffect(() => {
    setWaitStartedAt(videoId ? Date.now() : null);
  }, [videoId]);

  // `stalled` is derived from elapsed time, and time passes without producing an event to
  // render on: Apollo only notifies subscribers when a poll result differs from the last
  // one, so a video sitting in PROCESSING with unchanging data never re-renders. Tick
  // while we're waiting so the ceiling is actually noticed.
  const [, setClockTick] = useState(0);

  // Drive the UI off the VideoById response. The thumbnail is only a preview — it has no
  // say in whether the video is ready.
  const { video } = data || {};
  const thumbnailUrl = video?.thumbnailUrl;
  // The server answered successfully and had no such video (deleted, or not visible to
  // this company). Requiring `!error` matters: an errored response also carries a null
  // video, and that one is transient — we keep polling through it instead.
  const isMissing = !loading && !error && !!data && !video;
  const state = videoInputState({
    videoId,
    status: video?.status,
    isMissing,
    hasStalled: hasProcessingStalled(video?.createdAt ?? waitStartedAt),
  });
  const ready = state === "ready";
  const failed = state === "failed";
  const processing = state === "processing";
  const stalled = state === "stalled";
  const unavailable = state === "unavailable";
  const waiting = shouldPollVideo(state);

  useEffect(() => {
    if (!waiting) return;
    const clock = setInterval(() => setClockTick((t) => t + 1), CLOCK_TICK_MS);
    return () => clearInterval(clock);
  }, [waiting]);

  // Keep polling until the video actually resolves. `stalled` deliberately still polls —
  // it only changes what we tell the user — so a slow transcode that finishes past the
  // ceiling is still picked up. Backing off once stalled keeps the long tail cheap: every
  // errored poll also reports to Rollbar, so the slow lane matters.
  useEffect(() => {
    if (!videoId) return;
    if (waiting) {
      startPolling(stalled ? STALLED_POLL_MS : POLL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [videoId, waiting, stalled, startPolling, stopPolling]);

  const onDropAccepted = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      setUploading(true);
      try {
        const fileInfo = await handleFileUpload(file, 'video');
        if (!fileInfo?.public_id) {
          throw new Error('Upload failed. Please try again.');
        }

        const res = await createVideo({
          variables: {
            record: {
              name: fileInfo.original_filename,
              videoDownloadUrl: cld.video(fileInfo.public_id).toURL()
            }
          }
        });

        if (!res.data?.video?._id) {
          throw new Error('Could not save the video. Please try again.');
        }

        // Persist the DB video id immediately so the teaser can be saved while
        // the video keeps processing.
        setValue(name, res.data.video._id, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      } catch (e: any) {
        console.error(e);
        message.error(e?.message || 'Video upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
    },
    [name, setValue, createVideo]
  );

  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      if (fileRejections[0].errors[0].code === ErrorCode.FileTooLarge) {
        message.warning(`File is larger than ${MAX_VIDEO_SIZE_MB}MB.`)
      }
    }
  , [])

  const { getRootProps, getInputProps, isDragActive, isDragAccept } =
    useDropzone({
      accept: { "video/*": [] },
      multiple: false,
      disabled: readOnly,
      maxSize: MAX_VIDEO_SIZE_MB * 1024 * 1024,
      onDropAccepted,
      onDropRejected
    });

  const remove = () => {
    setValue(name, null, { shouldDirty: true, shouldTouch: true });
  };

  const removeButton = (
    <button
      type="button"
      className="absolute -top-2 -right-2 inline-flex items-center p-0 border border-transparent rounded-full shadow-sm text-white bg-gray-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      onClick={remove}
    >
      <XMarkIcon className="h-4 w-4" aria-hidden="true" />
    </button>
  );

  return (
    <>
      {/* The hidden input exists only to register the field. react-hook-form seeds a
          registered field from its DOM node when it has no default, which hands back "" —
          and videoId is an ObjectId, where "" is a coercion error rather than an empty
          value. Map that one value to null, and leave everything else alone. */}
      <input type="hidden" {...register(name, { required, setValueAs: (v) => (v === '' ? null : v) })} />

      {/* Uploading to storage (before we have a video id) */}
      {uploading && (
        <div className={CONTAINER_CLASS + " border-gray-300"}>
          <div className="text-center">
            <div className="animate-bounce bg-white dark:bg-slate-800 p-2 w-10 h-10 ring-1 ring-slate-900/5 dark:ring-slate-200/20 shadow-lg rounded-full flex items-center justify-center mx-auto">
              <ArrowUpIcon className="w-6 h-6 text-violet-500" />
            </div>
            <p className="text-sm mt-2">Uploading</p>
          </div>
        </div>
      )}

      {/* Ready. The thumbnail may lag behind the status, so fall back to a plain card
          rather than rendering a broken <img>. */}
      {!uploading && ready && thumbnailUrl && (
        <div className="relative inline-flex">
          <img src={thumbnailUrl} alt="Thumbnail" loading="lazy" style={{ height: 140 }} />
          {removeButton}
        </div>
      )}

      {!uploading && ready && !thumbnailUrl && (
        <div className={CONTAINER_CLASS + " border-gray-300 text-center"}>
          {removeButton}
          <p className="text-sm">Video ready</p>
          <p className="text-xs text-gray-500 mt-1">The preview image isn't available yet.</p>
        </div>
      )}

      {/* Failed (from the VideoById response) */}
      {!uploading && failed && (
        <div className={CONTAINER_CLASS + " border-red-300 text-center"}>
          {removeButton}
          <p className="text-sm text-red-600 font-medium">Video upload failed.</p>
          <button type="button" className="text-sm text-indigo-600 hover:text-indigo-500 mt-1" onClick={remove}>
            Remove and try again
          </button>
        </div>
      )}

      {/* Processing — thumbnail not ready yet. The teaser can still be saved. */}
      {!uploading && processing && (
        <div className={CONTAINER_CLASS + " border-gray-300"}>
          {removeButton}
          <div className="text-center">
            <div className="animate-bounce bg-white dark:bg-slate-800 p-2 w-10 h-10 ring-1 ring-slate-900/5 dark:ring-slate-200/20 shadow-lg rounded-full flex items-center justify-center mx-auto">
              <Spinner className="" size={SPINNER_SIZES.SMALL} />
            </div>
            <p className="text-sm mt-2">Processing</p>
            <p className="text-xs text-gray-500 mt-1">You can save this teaser while the video is processing.</p>
          </div>
        </div>
      )}

      {/* Past the ceiling. We're still checking — this only stops us claiming the video
          is on track. The teaser is savable either way. */}
      {!uploading && stalled && (
        <div className={CONTAINER_CLASS + " border-gray-300 text-center"}>
          {removeButton}
          <p className="text-sm">Still processing</p>
          <p className="text-xs text-gray-500 mt-1">
            This is taking longer than usual. We'll keep checking — you can save this teaser
            and come back later.
          </p>
        </div>
      )}

      {/* The server has no such video. Polling can't fix this one. */}
      {!uploading && unavailable && (
        <div className={CONTAINER_CLASS + " border-red-300 text-center"}>
          {removeButton}
          <p className="text-sm text-red-600 font-medium">This video is no longer available.</p>
          <button type="button" className="text-sm text-indigo-600 hover:text-indigo-500 mt-1" onClick={remove}>
            Remove and upload again
          </button>
        </div>
      )}

      {/* No video yet → upload dropzone */}
      {!uploading && !videoId && (
        <div
          className={
            CONTAINER_CLASS + " " +
            (isDragActive
              ? isDragAccept
                ? "border-blue-600"
                : "border-red-600"
              : "border-gray-300")
          }
          {...getRootProps()}
        >
          <div className="space-y-1 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {!isDragActive && (
              <>
                <div className="flex text-sm text-gray-600">
                  <span className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                    <span>Upload a video</span>
                    <input className="sr-only" {...getInputProps()} />
                  </span>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">Up to {MAX_VIDEO_SIZE_MB} MB</p>
              </>
            )}
            {isDragActive && <div className="flex text-sm">&nbsp;</div>}
            {isDragActive && isDragAccept && (
              <p className="text-xs text-green-600 font-medium">
                Drop video to upload
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VideoInput;
